import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import { RootState } from "_shared/_stores/root-reducer";
import { AppDispatch } from "_shared/_stores/store-types";
import { calendarEventListTag, calendarEventsApi } from "calendar/_api/calendar-event-api";
import Guid from "_shared/guids/guid";
import { messagingApi } from "messaging/_api/messaging-api";
import { populateForumsByTopic } from "messaging/forums/_stores/forums-redux-slice";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import Session from "users/__session/session";
import {
  invalidateWorkAgreementListApiCaches,
  invalidateWorkAgreementByIdApiCache,
  invalidateProposalsAWorkRosterChangeMoves,
  removeWorkAgreementApiCaches,
} from "work/work-agreements/_api/work-agreement-api";
import { removeWorkAgreement } from "work/work-agreements/_store/work-agreement-redux-slice";
import { invalidateWorkAgreementDocuments } from "work/work-agreements/documents/_api/work-agreement-documents-api";

/**
 * Asks again for the chats an agreement carries. A roster change reaches the chats through a durable
 * outbox some seconds after the agreement's own `work-agreement-updated`, so the refetch that notice
 * makes usually reads the rooms as they were. `work-agreement-chats-changed` follows once the rooms
 * hold the new roster, and asks again (#5783). The agreement already in authorized local state
 * identifies a shared revision conversation; otherwise the agreement's own ID is the conversation
 * fallback.
 */
const refreshWorkAgreementForums = ({
  dispatch,
  getState,
  session,
  workAgreementIdValue,
}: {
  dispatch: AppDispatch;
  getState: () => RootState;
  session: Session;
  workAgreementIdValue: string;
}) => {
  // Every live forums query - the dashboard's unread conversations among them - asks again.
  dispatch(messagingApi.util.invalidateTags([{ type: "Forum", id: "LIST" }]));

  // The agreement page reads its chats from the forums slice, which holds no live subscription, so
  // the invalidation alone never reaches it. Only a topic somebody has loaded is fetched again.
  const currentAgreement = getState().workAgreements.byId[workAgreementIdValue];
  const topic = Topic.create({
    entityClass: new ForumEntityClass("Work.Agreement"),
    entityId: new Guid(currentAgreement?.conversationWorkAgreementId ?? workAgreementIdValue),
  });
  if (getState().forums.byTopic.entries[topic.toString()] === undefined) return;
  dispatch(populateForumsByTopic({ session, topic, forceRefetch: true }));
};

const getWorkAgreementIdValue = (payload: unknown): string | undefined => {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object" && "id" in payload) {
    const id = (payload as { id?: unknown }).id;
    return typeof id === "string" ? id : undefined;
  }

  return undefined;
};

const handleWorkAgreementRemoval = (payload: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
  dispatch(calendarEventsApi.util.invalidateTags([calendarEventListTag]));
  const workAgreementIdValue = getWorkAgreementIdValue(payload);
  if (!workAgreementIdValue) {
    throw new Error("Received work-agreement-deleted event with no work agreement ID");
  }

  const session = Session.loadFromStorage(() => {});
  removeWorkAgreementApiCaches({ dispatch, getState, session, workAgreementIdValue });
  dispatch(removeWorkAgreement(new Guid(workAgreementIdValue)));
};

const handleWorkAgreementUpsert = (payload: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
  const workAgreementIdValue = getWorkAgreementIdValue(payload);
  if (!workAgreementIdValue) {
    throw new Error("Received work agreement invalidation with no work agreement ID");
  }

  dispatch(calendarEventsApi.util.invalidateTags([calendarEventListTag]));
  const session = Session.loadFromStorage(() => {});
  refreshWorkAgreementForums({ dispatch, getState, session, workAgreementIdValue });
  invalidateWorkAgreementListApiCaches(dispatch);
  invalidateWorkAgreementByIdApiCache({ dispatch, workAgreementIdValue });
  // Taken off the work, or brought back onto it, by somebody else.
  invalidateProposalsAWorkRosterChangeMoves({
    dispatch,
    sourceProposalIdValue: getState().workAgreements.byId[workAgreementIdValue]?.sourceProposalId ?? undefined,
  });
};

/** The agreement's chats now seat the roster it was saved with (#5783): the chat list asks again. */
const handleWorkAgreementChatsChanged = (payload: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
  const workAgreementIdValue = getWorkAgreementIdValue(payload);
  if (!workAgreementIdValue) {
    throw new Error("Received work-agreement-chats-changed event with no work agreement ID");
  }

  const session = Session.loadFromStorage(() => {});
  refreshWorkAgreementForums({ dispatch, getState, session, workAgreementIdValue });
};

/** Somebody on the work added or removed one of its documents (#6052): the Documents tab asks again. */
const handleWorkAgreementDocumentsChanged = (payload: unknown) => (dispatch: AppDispatch) => {
  const workAgreementIdValue = getWorkAgreementIdValue(payload);
  if (!workAgreementIdValue) {
    throw new Error("Received work-agreement-documents-changed event with no work agreement ID");
  }

  invalidateWorkAgreementDocuments(dispatch);
};

const callbacks = withCallbacks<RootState, AppDispatch>()
  .add("work-agreement-created", handleWorkAgreementUpsert)
  .add("work-agreement-updated", handleWorkAgreementUpsert)
  .add("work-agreement-deleted", handleWorkAgreementRemoval)
  .add("work-agreement-documents-changed", handleWorkAgreementDocumentsChanged)
  .add("work-agreement-chats-changed", handleWorkAgreementChatsChanged);

export const workAgreementsAblyReduxMiddleware = reduxAblyMiddleware<RootState, AppDispatch>({
  callbacks,
  channelName: ablyChannelNamesForSession.workAgreements,
  connectionHealth: { key: "workAgreements", label: "Work Agreements" },
  shouldConnectionStartImmediately: true,
});

